import type { ReactNode } from 'react';
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authService } from '../services/authService';
import type { AuthState } from '../types/AuthTypes';

interface LoginResult {
  requiresTwoFactor: boolean;
  mustSetup2FA: boolean;
  setupToken?: string;
  mustResetPassword?: boolean;
}

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<LoginResult>;
  loginWith2FA: (email: string, password: string, totpCode: string) => Promise<LoginResult>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    email: null,
    role: null,
    permissions: [],
    isLoading: true,
  });

  // On mount, check for existing valid token
  useEffect(() => {
    const token = authService.getAccessToken();
    if (token && !authService.isTokenExpired(token)) {
      const payload = authService.parseToken(token);
      if (payload) {
        setState({
          isAuthenticated: true,
          email: payload.unique_name,
          role: payload.role,
          permissions: authService.extractPermissions(payload),
          isLoading: false,
        });
        return;
      }
    }

    // Try silent refresh
    const refreshToken = authService.getRefreshToken();
    if (refreshToken) {
      authService.refresh()
        .then(res => {
          authService.saveTokens(res.accessToken, res.refreshToken);
          const payload = authService.parseToken(res.accessToken);
          setState({
            isAuthenticated: true,
            email: payload?.unique_name || null,
            role: payload?.role || null,
            permissions: authService.extractPermissions(payload),
            isLoading: false,
          });
        })
        .catch(() => {
          authService.clearTokens();
          setState({ isAuthenticated: false, email: null, role: null, permissions: [], isLoading: false });
        });
    } else {
      setState(s => ({ ...s, isLoading: false }));
    }
  }, []);

  const login = useCallback(async (email: string, password: string): Promise<LoginResult> => {
    const response = await authService.login({ email, password });

    if (response.mustSetup2FA) {
      return { requiresTwoFactor: false, mustSetup2FA: true, setupToken: response.setupToken };
    }

    if (response.requiresTwoFactor) {
      return { requiresTwoFactor: true, mustSetup2FA: false };
    }

    // Direct login (shouldn't happen with mandatory 2FA, but handle gracefully)
    if (response.accessToken && response.refreshToken) {
      authService.saveTokens(response.accessToken, response.refreshToken);
      const payload = authService.parseToken(response.accessToken);
      setState({
        isAuthenticated: true,
        email: response.email || null,
        role: response.role || null,
        permissions: authService.extractPermissions(payload),
        isLoading: false,
      });
    }

    return {
      requiresTwoFactor: false,
      mustSetup2FA: false,
      mustResetPassword: response.mustResetPassword,
    };
  }, []);

  const loginWith2FA = useCallback(async (email: string, password: string, totpCode: string): Promise<LoginResult> => {
    const response = await authService.loginWith2FA({ email, password, totpCode });
    if (response.accessToken && response.refreshToken) {
      authService.saveTokens(response.accessToken, response.refreshToken);
      const payload = authService.parseToken(response.accessToken);
      setState({
        isAuthenticated: true,
        email: response.email || null,
        role: response.role || null,
        permissions: authService.extractPermissions(payload),
        isLoading: false,
      });
    }
    return {
      requiresTwoFactor: false,
      mustSetup2FA: false,
      mustResetPassword: response.mustResetPassword,
    };
  }, []);

  const logout = useCallback(async () => {
    await authService.logout();
    setState({ isAuthenticated: false, email: null, role: null, permissions: [], isLoading: false });
  }, []);

  return (
    <AuthContext.Provider value={{ ...state, login, loginWith2FA, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
